import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { itemIdOf } from "../../../../api/content/processDefinitions";
import type { WorkflowListing } from "../../../../api/content/workflowsApi";
import { switchWorkflow, type MadeOutput, type OutputChoice, type SwitchWorkflow, type WorkflowSwitchOptions } from "../../../../api/threads/workflowSwitchApi";
import ErrorNotice from "../../../../errors/ErrorNotice";
import { useSubmission } from "../../../../shared/state/useSubmission";
import AppButton from "../../../../ui/buttons/AppButton";
import StatusMessage from "../../../../ui/feedback/StatusMessage";
import Field from "../../../../ui/forms/Field";
import { FORM_CONTROL_CLASS, FORM_HELP_TEXT_CLASS } from "../../../../ui/styles/formStyles";
import StartPlan from "../../prepare/run/StartPlan";
import WorkflowChoice from "../../prepare/run/WorkflowChoice";
import { valueText } from "../../status/valueText";
import { useThreadContext } from "../threadPageContext";
import { useFocusOnInputs } from "../../prepare/run/useFocusOnInputs";
import { inputLabel, madeChoiceText, switchBlocker, switchSummary, switchSummaryLines, withProducerNames } from "./switchWords";
import { usePreparedSwitch } from "./useWorkflowSwitch";

const NO_CHOICES: Readonly<Record<string, OutputChoice>> = {};
const NO_VALUES: Readonly<Record<string, unknown>> = {};

interface WorkflowSwitchProps {
  options: WorkflowSwitchOptions;
  /** The Workflows of the Thread's content version, as every chooser lists them; `null` while they load. */
  listing: WorkflowListing | null;
  lastEntryNumber: number;
  onClose: () => void;
}

/**
 * Switching the Thread to another Workflow, inline in the steps (F126; no modal, F31). The
 * start's chooser lists the compatible Workflows, each saying what it uses of what the Thread
 * made and what it still misses (`switchWords`); a pick closes the list to its card (F98) and
 * shows its inputs as the start does, the carried outputs filled in, with a choice where
 * several fit. The server decides what fits and what is carried; this renders it.
 */
export default function WorkflowSwitch({ options, listing, lastEntryNumber, onClose }: WorkflowSwitchProps) {
  const { t } = useTranslation();
  const { threadId, overview, openTurn, openHumanStepEntry, stepNameIn } = useThreadContext();
  const made = withProducerNames(options.made, stepNameIn);
  const requestKey = useId();
  const [picked, setPicked] = useState<string | null>(null);
  const [carried, setCarried] = useState(NO_CHOICES);
  const [values, setValues] = useState(NO_VALUES);
  const { state: submission, submit } = useSubmission();
  const compatible = new Map(options.workflows.map((workflow) => [workflow.reference, workflow]));
  const summaries = listing?.workflows.filter((summary) => compatible.has(summary.reference)) ?? [];
  const chosen = summaries.find((summary) => itemIdOf(summary.reference) === picked) ?? null;
  const prepared = usePreparedSwitch({ threadId, workflow: chosen?.reference ?? null, carried, values, lastEntryNumber });
  const plan = prepared.status === "loaded" ? prepared.value : prepared.status === "loading" ? prepared.previous : null;
  // A pick moves the focus to the Workflow's first input once it is prepared, as at a start (F98).
  const { inputsRef, picked: focusInputs } = useFocusOnInputs(prepared.status === "loaded");

  if (options.refused === "batch_item") {
    return <StatusMessage tone="info"><p>{t("threads.switch.batchItem")}</p></StatusMessage>;
  }
  const switching = submission.status === "submitting" || submission.status === "succeeded";
  const busy = openTurn !== null;
  const blocker = plan === null ? null : switchBlocker(switchSummary(plan, made, t), t);
  const ready = chosen !== null && prepared.status === "loaded" && blocker === null && !busy;
  const missing = busy ? t("threads.switch.busy") : blocker;
  // The chosen Workflow is worded from its own preparation with the person's choices; the
  // others from the list's, each with what it would carry.
  const detailOf = (reference: string) => {
    const isChosen = chosen !== null && reference === chosen.reference && plan !== null;
    const workflow = isChosen ? plan : compatible.get(reference);
    if (workflow === undefined) return null;
    return switchSummaryLines(switchSummary(workflow, made, t), t, !isChosen).map((line) => <span key={line} className="block">{line}</span>);
  };

  const switchNow = () => {
    if (chosen === null) return;
    submit(async () => {
      await switchWorkflow(threadId, { workflow: chosen.reference, carried, inputValues: values, expectedLastEntryNumber: lastEntryNumber });
      onClose();
    });
  };

  return (
    <div className="space-y-4">
      <WorkflowChoice
        projectId={overview.projectId}
        listing={listing === null ? null : { ...listing, workflows: summaries }}
        pending={null}
        fitted={true}
        workflow={chosen}
        cameWithWorkflow={false}
        requestKey={requestKey}
        disabled={switching}
        onPick={(item) => { setPicked(item); setCarried(NO_CHOICES); setValues(NO_VALUES); focusInputs(); }}
        detailOf={(candidate) => detailOf(candidate.reference)}
        offersFree={false}
        emptyText={t("threads.switch.none")}
      />
      <div ref={inputsRef} tabIndex={-1} className="space-y-4 outline-none">
        {plan === null ? null : <CarriedChoices workflow={plan} made={made} chosen={carried} disabled={switching} onChoose={setCarried} />}
        {chosen === null ? null : <StartPlan projectId={overview.projectId} state={prepared} values={values} onValuesChange={setValues} disabled={switching} />}
      </div>
      {chosen !== null && openHumanStepEntry !== null ? (
        <StatusMessage tone="warning"><p>{t("threads.switch.withdraws")}</p></StatusMessage>
      ) : null}
      {submission.status === "failed" ? <ErrorNotice error={submission.error} /> : null}
      <div className="flex flex-wrap items-center justify-end gap-3">
        {chosen === null || missing === null ? null : <p id={`${requestKey}-missing`} className={FORM_HELP_TEXT_CLASS}>{missing}</p>}
        <AppButton variant="secondary" size="sm" onClick={onClose} disabled={switching}>{t("common.cancel")}</AppButton>
        {chosen === null ? null : (
          <AppButton variant="primary" size="sm" onClick={switchNow} disabled={!ready || switching} aria-describedby={missing === null ? undefined : `${requestKey}-missing`}>
            {switching ? t("threads.switch.switching") : t("threads.switch.confirm", { workflow: chosen.name })}
          </AppButton>
        )}
      </div>
    </div>
  );
}

interface CarriedChoicesProps {
  workflow: SwitchWorkflow;
  made: readonly MadeOutput[];
  chosen: Readonly<Record<string, OutputChoice>>;
  disabled: boolean;
  onChoose: (chosen: Readonly<Record<string, OutputChoice>>) => void;
}

/** Where several outputs made so far fit an input, which one it takes: the newest unless the person picks another. */
function CarriedChoices({ workflow, made, chosen, disabled, onChoose }: CarriedChoicesProps) {
  const { t } = useTranslation();
  const choices = workflow.carried.filter((carried) => carried.candidates.length > 1);
  if (choices.length === 0) return null;
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {choices.map((carried) => (
        <Field key={carried.destination} label={t("threads.switch.outputFor", { input: inputLabel(workflow, carried.destination) })}>
          {(control) => (
            <select
              {...control}
              className={FORM_CONTROL_CLASS}
              value={carried.output}
              disabled={disabled}
              onChange={(event) => {
                const output = made[Number(event.target.value)];
                if (output !== undefined) onChoose({ ...chosen, [carried.destination]: { entry: output.entry, part: output.part } });
              }}
            >
              {carried.candidates.map((index) => {
                const output = made[index];
                return output === undefined ? null : <option key={index} value={index} title={valueText(output.value, t)}>{madeChoiceText(output, t)}</option>;
              })}
            </select>
          )}
        </Field>
      ))}
    </div>
  );
}
